import { useState } from 'react'
import type { JSX } from 'react'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  IconButton,
  Switch,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useCurrentOrganization,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'

import { useUpdateOrganizationEnforce2FA } from '../../hooks/useUpdateOrganizationEnforce2FA'

import styles from './TeamHeaderMenu.module.css'

const CHANGE_OWNER_URL =
  'https://support.buffer.com/en-us/articles/changing-the-owner-of-your-buffer-account-aCU4tRKZGW'
// @todo Point at the real Help Center article once written.
const ABOUT_ROLES_URL = 'https://support.buffer.com/'

export const TeamHeaderMenu = (): JSX.Element | null => {
  const { account } = useAccount()
  const currentOrganization = useCurrentOrganization()
  const { updateEnforce2FA, loading } = useUpdateOrganizationEnforce2FA()
  const canTransferOwnership = useHasOrganizationAction('transferOwnership')
  const canManageEnforce2FA = useHasOrganizationAction(
    'manageSecurityTFAEnforcement',
  )

  const organizationId = currentOrganization?.id
  const initialEnforce2FA = currentOrganization?.settings?.enforce2FA ?? false
  // The acting admin needs 2FA themselves before requiring it for the team,
  // or the server-side gate would lock them out on their next request. The
  // server enforces this too; disabling here is a UX hint.
  const actorHasTwoFactor = Boolean(account?.tfa?.type)

  const [enforce2FA, setEnforce2FA] = useState(initialEnforce2FA)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  if (!organizationId) return null

  // Once enforcement is on, the admin must always be able to turn it off.
  const needsSelfTwoFactor = !enforce2FA && !actorHasTwoFactor
  const toggleDisabled = loading || needsSelfTwoFactor
  const toggleDescription = needsSelfTwoFactor
    ? 'Set up 2FA on your own account before requiring it for your team.'
    : 'Every team member must have 2FA set up to use Buffer.'

  const apply = async (next: boolean): Promise<void> => {
    setEnforce2FA(next)
    try {
      await updateEnforce2FA({ organizationId, enforce2FA: next })
    } catch {
      setEnforce2FA(!next)
    }
  }

  const handleToggleSelect = (event: Event): void => {
    if (enforce2FA) {
      // Turning enforcement off is safe: apply it and keep the menu open so
      // the switch is seen flipping.
      event.preventDefault()
      void apply(false)
      return
    }
    // Turning it on locks out members without 2FA, so let the menu close and
    // confirm first.
    setIsConfirmOpen(true)
  }

  const handleConfirmEnable = (): void => {
    setIsConfirmOpen(false)
    void apply(true)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <IconButton variant="secondary" label="More team options">
            <EllipsisVerticalIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" className={styles.content}>
          {canTransferOwnership && (
            <DropdownMenu.Item asChild className={styles.item}>
              <a
                href={CHANGE_OWNER_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Change Organization Owner
                <ExternalLinkIcon />
              </a>
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item asChild className={styles.item}>
            <a href={ABOUT_ROLES_URL} target="_blank" rel="noopener noreferrer">
              About Roles
              <ExternalLinkIcon />
            </a>
          </DropdownMenu.Item>
          {canManageEnforce2FA && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                className={styles.item}
                disabled={toggleDisabled}
                onSelect={handleToggleSelect}
                description={toggleDescription}
              >
                Require two-factor authentication
                {/* The menu item is the control; the switch only reflects state. */}
                <Switch
                  className={styles.switch}
                  size="small"
                  checked={enforce2FA}
                  disabled={toggleDisabled}
                  tabIndex={-1}
                  aria-hidden
                />
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialog.Content>
          <AlertDialog.Title>Require 2FA for the Whole Team?</AlertDialog.Title>
          <AlertDialog.Description>
            Existing team members who don&apos;t have 2FA set up will be locked
            out of Buffer on their next request until they complete 2FA setup.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary">Cancel</Button>
            </AlertDialog.Cancel>
            <Button
              variant="primary"
              loading={loading}
              disabled={loading}
              onClick={handleConfirmEnable}
            >
              Require 2FA
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}
